import type { CSSProperties, ReactNode, MouseEvent } from 'react';
import { Icon } from './Icon';
import type { Badge as BadgeT } from '../lib/status';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BTN_BASE: CSSProperties = {
  appearance: 'none',
  cursor: 'pointer',
  fontFamily: 'inherit',
  fontWeight: 600,
  fontSize: 13,
  borderRadius: 8,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 5,
  whiteSpace: 'nowrap',
};

const VARIANTS: Record<Variant, CSSProperties> = {
  primary: { border: 0, background: 'var(--accent)', color: '#fff', padding: '8px 14px', boxShadow: 'var(--shadow-sm)' },
  secondary: { border: '1px solid var(--border-2)', background: 'var(--surface)', color: 'var(--text)', padding: '8px 14px' },
  ghost: { border: 0, background: 'transparent', color: 'var(--text-2)', padding: '8px 12px' },
  danger: { border: '1px solid var(--danger)', background: 'var(--danger-bg)', color: 'var(--danger)', padding: '8px 14px' },
};

interface ButtonProps {
  children: ReactNode;
  variant?: Variant;
  icon?: string;
  onClick?: (e: MouseEvent) => void;
  type?: 'button' | 'submit';
  style?: CSSProperties;
  title?: string;
  disabled?: boolean;
  full?: boolean;
}

export function Button({ children, variant = 'primary', icon, onClick, type = 'button', style, title, disabled, full }: ButtonProps) {
  return (
    <button
      type={type}
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{ ...BTN_BASE, ...VARIANTS[variant], width: full ? '100%' : undefined, opacity: disabled ? 0.5 : 1, ...style }}
    >
      {icon && <Icon name={icon} size={18} />}
      {children}
    </button>
  );
}

export function StatusBadge({ badge, pill = true }: { badge: BadgeT; pill?: boolean }) {
  return (
    <span
      style={{
        fontSize: 11.5,
        fontWeight: 600,
        padding: '3px 9px',
        borderRadius: pill ? 20 : 6,
        background: badge.bg,
        color: badge.fg,
        whiteSpace: 'nowrap',
        display: 'inline-block',
      }}
    >
      {badge.label}
    </span>
  );
}

export function Avatar({ initials, bg, fg, size = 34, ring }: { initials: string; bg: string; fg: string; size?: number; ring?: boolean }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        flex: '0 0 auto',
        borderRadius: '50%',
        background: bg,
        color: fg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: size * 0.37,
        border: ring ? '2px solid var(--surface)' : undefined,
      }}
    >
      {initials}
    </div>
  );
}

export function Card({ children, style, onClick, hover }: { children: ReactNode; style?: CSSProperties; onClick?: (e: MouseEvent) => void; hover?: boolean }) {
  return (
    <div
      onClick={onClick}
      className={hover ? 'cc-hover-card cc-clickable' : undefined}
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 12,
        boxShadow: 'var(--shadow-sm)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export function ProgressBar({ pct, width }: { pct: number; width?: number }) {
  return (
    <div style={{ width }}>
      <div style={{ height: 6, borderRadius: 4, background: 'var(--surface-3)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${pct}%`, background: 'var(--accent)', borderRadius: 4 }} />
      </div>
      <div className="cc-mono" style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>{pct}%</div>
    </div>
  );
}

export function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="cc-mono" style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-2)', background: 'var(--surface-2)', border: '1px solid var(--border)', padding: '2px 7px', borderRadius: 5 }}>
      {children}
    </span>
  );
}
